// ШАБ.1: единый компонент карты юнита (по mockup из data-source/card-mockups).
//
// Используется:
//   - В CrateOpeningScene (вылет карт из ШК), variant="compact".
//   - В DeckEditor каталоге (плитка), variant="compact".
//   - В CardDetailModalV2 (полный просмотр), variant="full".
//
// Цвета фракции через CSS-переменные --c1/--c2/--cInk на корневом
// элементе (data-faction). Дизайн един для всех 4 корп.

import React, { useState } from 'react';
import type { CardData } from '../lib/cardData';
import { useTr } from '../i18n/useTr';
import { trCardName, trCardAbility } from '../i18n/serverStrings';
import { IcoTaunt, IcoStealth, IcoSilence, IcoInfected1, IcoImmune, IcoSuppression, IcoPierce } from '../icons/keywordIcons';
import styles from './GameCard.module.css';

type Variant = 'compact' | 'full';
type StatusBadge = 'taunt' | 'silence' | null;

interface Props {
  card: CardData;
  variant?: Variant;
  /** Бейдж справа в шапке (Провокация/Молчание). По умолчанию null. */
  status?: StatusBadge;
  /** Текущий приоритет атаки. Если undefined — бейдж не показывается.
   *  Рендерится только для Глобал-юнитов (на других фракциях не имеет смысла —
   *  у них фракционный дефолт без выбора игроком). */
  priorityLabel?: string;
  /** Счётчик в правом верхнем углу (например "2/3" для каталога). */
  cornerCount?: string;
  /** Сделать карту faded (не в коллекции). */
  dim?: boolean;
  /** Glow по редкости (для крупных вылетающих карт после landing). */
  glowIntensity?: number; // 0..1
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}

const FACTION_FULL: Record<string, string> = {
  deus:      'DEUS SYSTEMS',
  syntetics: 'SYNTETICS SYNDICATE',
  global:    'GLOBAL SECURITY',
  livlife:   'LIV LIFE',
  neutral:   'УЛИЦА'
};
// БУГ.4/8: маппинг id приоритета → текст для бейджа карты.
// Покрывает и выбор игрока (Глобал), и дефолты фракций (см. engine priorities.js).
function labelForPriority(id: string, isEn: boolean): string {
  switch (id) {
    case 'strongest':         return isEn ? 'STRONGEST' : 'СИЛЬНЕЙШИЙ';
    case 'weakest':           return isEn ? 'WEAKEST'   : 'СЛАБЕЙШИЙ';
    case 'wounded':           return isEn ? 'WOUNDED'   : 'РАНЕНЫЙ';
    case 'lowest_hp':
    case 'lowest_current_hp': return isEn ? 'LOW HP'    : 'С НАИМ. HP';
    case 'healer':            return isEn ? 'HEALER'    : 'ХИЛЕР';
    case 'random':            return isEn ? 'RANDOM'    : 'СЛУЧАЙНО';
    default:                  return id.toUpperCase();
  }
}

// БУГ.8: дефолты приоритета атаки по фракции (зеркалит DEFAULTS_BY_FACTION
// из src/engine/priorities.js). Используется для не-Глобал юнитов в каталоге.
const FACTION_PRIORITY_DEFAULTS: Record<string, string> = {
  deus:      'weakest',
  syntetics: 'random',
  global:    'random',           // в каталоге Глобала, до выбора игрока
  livlife:   'lowest_current_hp',
  neutral:   'weakest'
};

// Вычислить эффективный priority id для отображения на карте.
// Приоритет источников: card.attackPriority (Глобал, выбор игрока) →
// card.priority_override (override из xlsx) → дефолт фракции.
function effectivePriority(card: { faction: string; priority_override?: string; attackPriority?: string }): string {
  if (card.faction === 'global' && card.attackPriority) return card.attackPriority;
  if (card.priority_override) return card.priority_override;
  return FACTION_PRIORITY_DEFAULTS[card.faction] || 'weakest';
}

const FACTION_FULL_EN: Record<string, string> = {
  deus:      'DEUS SYSTEMS',
  syntetics: 'SYNTETICS SYNDICATE',
  global:    'GLOBAL SECURITY',
  livlife:   'LIV LIFE',
  neutral:   'STREET'
};

const RARITY_LETTER: Record<string, string> = {
  common: 'С', rare: 'R', epic: 'E', legendary: 'L', special: 'S'
};

// Арт карты. Пытается загрузить /img/cards/<id>.jpg.
// Если файла нет (404 / любой error) — fallback на CardTypeGlyph (SVG-силуэт).
// Размер JPG = размер мастера (юзер сам контролирует кадрирование).
function CardArt({ id, type, color }: { id: string; type: string; color: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <CardTypeGlyph type={type} color={color} />;
  return (
    <img
      src={`/img/cards/${id}.jpg`}
      alt=""
      onError={() => setFailed(true)}
      style={{
        position: 'absolute',
        top: 0, left: 0,
        width: '100%', height: '100%',
        objectFit: 'cover',
        pointerEvents: 'none'
      }}
    />
  );
}

// Иконка-плейсхолдер по типу карты (для арт-слота).
function CardTypeGlyph({ type, color }: { type: string; color: string }) {
  const common = { width: '50%', height: '60%', fill: 'rgba(255,255,255,0.05)', stroke: color, strokeWidth: 1.2 };
  if (type === 'unit') {
    return (
      <svg viewBox="0 0 100 100" style={common as any}>
        <circle cx={50} cy={36} r={14} />
        <path d="M 22 92 A 28 28 0 0 1 78 92" />
      </svg>
    );
  }
  if (type === 'action') {
    return (
      <svg viewBox="0 0 24 24" style={common as any}>
        <polygon points="13,2 3,14 11,14 9,22 21,10 13,10" />
      </svg>
    );
  }
  if (type === 'mod') {
    return (
      <svg viewBox="0 0 24 24" style={common as any}>
        <circle cx={12} cy={12} r={3} />
        <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" style={common as any}>
      <polygon points="12,2 22,12 12,22 2,12" />
    </svg>
  );
}

// Первый ненулевой блок ability_text для compact-режима (1-2 строки).
function shortAbility(card: CardData): string | null {
  const at = trCardAbility(card.ability_text, card.ability_text_en);
  const s = at.order || at.active || at.trigger || at.aura || at.deathrattle;
  if (!s) return null;
  return s.split('\n')[0].trim();
}

// Все блоки ability_text для full-режима.
function fullAbility(card: CardData, isEn: boolean): string {
  const at = trCardAbility(card.ability_text, card.ability_text_en);
  const parts: string[] = [];
  const lbl = (ru: string, en: string) => isEn ? en : ru;
  // Этап 63 — метки оставляем только у Приказа / Посмертия (+ Активка у Героев:
  // игрок её активирует). У Триггера / Ауры метку убрали — чистый текст эффекта.
  if (at.order)       parts.push(lbl('Приказ', 'Order') + ': ' + at.order);
  if (at.active)      parts.push(lbl('Активка', 'Active') + ': ' + at.active);
  if (at.trigger)     parts.push(at.trigger);
  if (at.aura)        parts.push(at.aura);
  if (at.deathrattle) parts.push(lbl('Посмертие', 'Deathrattle') + ': ' + at.deathrattle);
  return parts.join('\n').trim();
}

// Этап 63 — инлайн-иконки статов (те же пути, что в нижней плашке) для текста способностей.
function IcoStatAtk() {
  return (
    <svg className={styles.inlineStatIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinejoin="round">
      <path d="M14.5 17.5 3 6V3h3l11.5 11.5"/><path d="M13 19l6-6"/><path d="M16 16l4 4"/>
    </svg>
  );
}
function IcoStatArm() {
  return (
    <svg className={styles.inlineStatIcon} viewBox="0 0 24 24" fill="currentColor" fillOpacity={0.15} stroke="currentColor" strokeWidth={2.2} strokeLinejoin="round">
      <path d="M12 2 L20 5 V12 C20 17 16 21 12 22 C8 21 4 17 4 12 V5 Z"/>
    </svg>
  );
}
function IcoStatHp() {
  return (
    <svg className={styles.inlineStatIcon} viewBox="0 0 24 24" fill="currentColor" fillOpacity={0.2} stroke="currentColor" strokeWidth={2.2} strokeLinejoin="round">
      <path d="M12 21 C6 16 2 12 2 8 A4 4 0 0 1 12 6 A4 4 0 0 1 22 8 C22 12 18 16 12 21 Z"/>
    </svg>
  );
}

// Чип одного стата: иконка + цветное число (atk-красный / armor-золотой / hp-зелёный).
function StatChipInline({ kind, val, sign }: { kind: 'atk' | 'arm' | 'hp'; val: number; sign: string }) {
  const cls = kind === 'atk' ? styles.statAtk : kind === 'arm' ? styles.statArm : styles.statHp;
  return (
    <span className={`${styles.inlineStat} ${cls}`}>
      {kind === 'atk' ? <IcoStatAtk /> : kind === 'arm' ? <IcoStatArm /> : <IcoStatHp />}
      {sign}{val}
    </span>
  );
}

// Stat-тройка atk/armor/hp из текста («+0/0/2») → цветные чипы (показываем ненулевые).
function StatTriple({ sign, a, ar, h, kb }: { sign: string; a: number; ar: number; h: number; kb: string }) {
  const chips: React.ReactNode[] = [];
  if (a)  chips.push(<StatChipInline key={`${kb}a`} kind="atk" val={a} sign={sign} />);
  if (ar) chips.push(<StatChipInline key={`${kb}r`} kind="arm" val={ar} sign={sign} />);
  if (h)  chips.push(<StatChipInline key={`${kb}h`} kind="hp"  val={h} sign={sign} />);
  if (chips.length === 0) return <span className={styles.inlineStat}>{sign}0</span>;
  return <span className={styles.statTriple}>{chips}</span>;
}

const STAT_TRIPLE_RE = /([+\-]?)(\d+)\/(\d+)\/(\d+)/g;
const ABIL_LABEL_RE = /^(Приказ|Order|Посмертие|Deathrattle|Активка|Active):\s*/;

// Рендер строки описания: жирная метка (Приказ/Посмертие/Активка) + цветные stat-тройки.
function renderAbilityLine(line: string, keyBase: string): React.ReactNode {
  const out: React.ReactNode[] = [];
  let ni = 0;
  const pushText = (t: string) => { if (t) out.push(<React.Fragment key={`${keyBase}-${ni++}`}>{t}</React.Fragment>); };
  let rest = line;
  const lm = rest.match(ABIL_LABEL_RE);
  if (lm) {
    out.push(<strong key={`${keyBase}-${ni++}`} className={styles.abilLabel}>{lm[1]}:</strong>);
    pushText(' ');
    rest = rest.slice(lm[0].length);
  }
  let last = 0;
  let m: RegExpExecArray | null;
  STAT_TRIPLE_RE.lastIndex = 0;
  while ((m = STAT_TRIPLE_RE.exec(rest)) !== null) {
    pushText(rest.slice(last, m.index));
    out.push(<StatTriple key={`${keyBase}-${ni++}`} kb={`${keyBase}-${ni}`} sign={m[1]} a={+m[2]} ar={+m[3]} h={+m[4]} />);
    last = m.index + m[0].length;
  }
  pushText(rest.slice(last));
  return out;
}

// Человекочитаемые названия keyword'ов (для описания Модов).
const KW_LABELS: Record<string, [string, string]> = {
  stealth:     ['Скрытность', 'Stealth'],
  frenzy:      ['Неистовство', 'Frenzy'],
  immune:      ['Иммунитет', 'Immune'],
  pierce:      ['Пробитие', 'Pierce'],
  taunt:       ['Провокация', 'Taunt'],
  infected:    ['Заражён', 'Infected'],
  suppression: ['Подавление', 'Suppression']
};
// Иконки keyword'ов для чипов (значок + подпись). frenzy/rush — без иконки (только подпись).
const KW_ICONS: Record<string, React.ComponentType<{ width?: number; height?: number }>> = {
  taunt: IcoTaunt, stealth: IcoStealth, silence: IcoSilence,
  infected: IcoInfected1, immune: IcoImmune, suppression: IcoSuppression, pierce: IcoPierce,
};
function kwLabel(kw: string, isEn: boolean): string {
  const pair = KW_LABELS[kw];
  return pair ? (isEn ? pair[1] : pair[0]) : kw;
}

// Этап 56: описание Мода — его эффект (статы + keyword'ы + триггеры из ability_text).
// У Мода статы atk/armor/hp = БОНУС, который он даёт носителю (а не статы юнита).
function modEffect(card: CardData, isEn: boolean): string {
  const lbl = (ru: string, en: string) => isEn ? en : ru;
  const parts: string[] = [];
  const a = card.atk || 0, ar = card.armor || 0, h = card.hp || 0;
  if (a || ar || h) {
    parts.push(lbl('Навешивается на юнита: ', 'Attaches to a unit: ') + `+${a}/${ar}/${h}`);
  }
  if (Array.isArray(card.keywords) && card.keywords.length > 0) {
    parts.push(lbl('Даёт: ', 'Grants: ') + card.keywords.map(k => kwLabel(k, isEn)).join(', '));
  }
  const ft = fullAbility(card, isEn);
  if (ft) parts.push(ft);
  return parts.join('\n').trim();
}

export function GameCard({
  card,
  variant = 'compact',
  status = null,
  priorityLabel,
  cornerCount,
  dim = false,
  glowIntensity = 0,
  className = '',
  style,
  onClick
}: Props) {
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  const factionMap = isEn ? FACTION_FULL_EN : FACTION_FULL;
  const isFull = variant === 'full';
  const factionFull = factionMap[card.faction] || card.faction.toUpperCase();
  const rarityLetter = RARITY_LETTER[card.rarity] || '?';
  const abilityShort = shortAbility(card);
  const abilityFull = fullAbility(card, isEn);
  const isUnit = card.type === 'unit';
  // QA-фикс: карты Влияния (Корпшпион, Куратор операций и пр., type='influence')
  // — это юниты, выставляемые из стенда, у них есть atk/armor/hp. Раньше статы
  // рисовались только для type==='unit' → у карт Влияния статов не было видно.
  const hasStats = isUnit || card.type === 'influence';
  // БУГ.8: бейдж приоритета атаки рендерится для ВСЕХ юнитов в full-режиме.
  // У каждой фракции свой дефолт (см. FACTION_PRIORITY_DEFAULTS):
  //   - deus      → СЛАБЕЙШИЙ
  //   - syntetics → СЛУЧАЙНО (киберпсихи)
  //   - global    → СЛУЧАЙНО до выбора, либо выбранное игроком в бою
  //   - livlife   → С НАИМ. HP («самый раненый», диздок 7.4)
  //   - neutral   → СЛАБЕЙШИЙ
  // Можно override через priorityLabel prop (для случаев когда нужен
  // нестандартный текст).
  const showPriority = isFull && isUnit;
  const priorityLbl = showPriority
    ? (priorityLabel ?? labelForPriority(
        effectivePriority(card as unknown as { faction: string; priority_override?: string; attackPriority?: string }),
        isEn
      ))
    : null;
  const atk = card.atk ?? 0;
  const armor = card.armor ?? 0;
  const hp = card.hp ?? 0;
  const hasKeywords = Array.isArray(card.keywords) && card.keywords.length > 0;

  return (
    <div
      className={`${styles.card} ${styles[`faction-${card.faction}`]} ${styles[`rarity-${card.rarity}`]} ${styles[variant]} ${dim ? styles.dim : ''} ${className}`}
      style={{
        ...style,
        ...(glowIntensity > 0 ? { boxShadow: `inset 0 0 0 1px var(--c1), inset 0 0 0 3px rgba(0,0,0,0.5), inset 0 0 0 4px var(--c2), 0 10px 32px rgba(0,0,0,0.7), 0 0 ${20 + glowIntensity * 30}px var(--c1)` } : {})
      }}
      onClick={onClick}
    >
      <div className={styles.content}>
        {/* Шапка */}
        <div className={styles.header}>
          <div className={styles.headerCol}>
            <div className={styles.name}>{isEn && card.name_en ? card.name_en : trCardName(card.id, card.name_ru)}</div>
            <div className={styles.sub}>
              <span className={`${styles.rarity} ${styles[`r-${card.rarity}`]}`}>{rarityLetter}</span>
              <span className={styles.dot}> · </span>
              <span className={styles.type}>
                {card.type === 'unit'   ? tr('ЮНИТ', 'UNIT')
                 : card.type === 'action' ? tr('ДЕЙСТВИЕ', 'ACTION')
                 : card.type === 'mod'    ? tr('МОД', 'MOD')
                 : tr('КАРТА', 'CARD')}
              </span>
              {isFull && (
                <>
                  <span className={styles.dot}> · </span>
                  <span className={styles.corp}>{factionFull}</span>
                </>
              )}
            </div>
          </div>
          {/* Статус-бейдж (taunt/silence) — только в full. */}
          {isFull && status && (
            <div className={`${styles.statusBadge} ${styles[`status-${status}`]}`} title={status === 'taunt' ? tr('Провокация', 'Taunt') : tr('Молчание', 'Silence')}>
              {status === 'taunt' ? (
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M12 2 L22 6 V12 C22 17 17.5 21 12 22 C6.5 21 2 17 2 12 V6 Z"/></svg>
              ) : (
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path d="M5 9v6h4l5 4V5L9 9H5z" fill="currentColor"/>
                  <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth={2}/>
                </svg>
              )}
              <span className={styles.statusLbl}>{status === 'taunt' ? tr('ПРОВ.', 'TNT') : tr('МОЛЧ.', 'SIL')}</span>
            </div>
          )}
          {cornerCount && (
            <div className={styles.cornerCount}>{cornerCount}</div>
          )}
        </div>

        {/* Слот арта */}
        <div className={styles.artSlot}>
          <CardArt id={card.id} type={card.type} color="var(--c2)" />
          {/* Приоритет атаки — только для Глобал-юнитов с выбранным
              приоритетом (см. БУГ.4 выше). */}
          {showPriority && priorityLbl && (
            <div className={styles.priority}>
              <svg viewBox="0 0 24 24" width="10" height="10" fill="currentColor"><polygon points="12,2 22,8.5 22,15.5 12,22 2,15.5 2,8.5"/></svg>
              {priorityLbl}
            </div>
          )}
        </div>

        {/* Keywords — только в full-режиме (резервируем место даже если пусто). */}
        {isFull && (
          <div className={styles.keywords}>
            {hasKeywords ? (
              card.keywords.map((kw, i) => {
                const Ico = KW_ICONS[kw];
                return (
                  <span key={i} className={styles.keyword} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    {Ico && <Ico width={12} height={12} />}
                    {kwLabel(kw, isEn)}
                  </span>
                );
              })
            ) : (
              <span className={styles.keywordsEmpty}>{tr('— без ключевых слов —', '— no keywords —')}</span>
            )}
          </div>
        )}

        {/* Описание — только в full-режиме. Для Модов показываем их эффект
            (бонус-статы + keyword'ы), для остальных — блоки ability_text. */}
        {isFull && (() => {
          const descText = card.type === 'mod' ? modEffect(card, isEn) : abilityFull;
          // Этап 63: нет описания — НЕ рисуем блок вообще (убрали «— просто статы —»,
          // пустой бокс с рамкой смотрелся как потерянный контент).
          if (!descText) return null;
          return (
            <div className={styles.description}>
              {descText.split('\n').map((line, i) => <p key={i}>{renderAbilityLine(line, 'l' + i)}</p>)}
            </div>
          );
        })()}

        {/* Превью способности — только в compact, 1 строка под артом. */}
        {!isFull && abilityShort && (
          <div className={styles.compactAbility}>{abilityShort}</div>
        )}

        {/* Статы внизу — для юнитов И карт Влияния (юниты из стенда). */}
        {hasStats && (
          <div className={styles.stats}>
            <span className={styles.stat}>
              <svg className={`${styles.statIcon} ${styles.statAtk}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinejoin="round">
                <path d="M14.5 17.5 3 6V3h3l11.5 11.5"/>
                <path d="M13 19l6-6"/>
                <path d="M16 16l4 4"/>
              </svg>
              <span className={`${styles.statVal} ${styles.statAtk}`}>{atk}</span>
            </span>
            <span className={styles.stat}>
              <svg className={`${styles.statIcon} ${styles.statArm}`} viewBox="0 0 24 24" fill="currentColor" fillOpacity={0.15} stroke="currentColor" strokeWidth={2} strokeLinejoin="round">
                <path d="M12 2 L20 5 V12 C20 17 16 21 12 22 C8 21 4 17 4 12 V5 Z"/>
              </svg>
              <span className={`${styles.statVal} ${styles.statArm}`}>{armor}</span>
            </span>
            <span className={styles.stat}>
              <svg className={`${styles.statIcon} ${styles.statHp}`} viewBox="0 0 24 24" fill="currentColor" fillOpacity={0.2} stroke="currentColor" strokeWidth={2} strokeLinejoin="round">
                <path d="M12 21 C6 16 2 12 2 8 A4 4 0 0 1 12 6 A4 4 0 0 1 22 8 C22 12 18 16 12 21 Z"/>
              </svg>
              <span className={`${styles.statVal} ${styles.statHp}`}>{hp}</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
